import { toast } from "sonner";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Megaphone, CheckCircle2, Lock, Loader2, ArrowRight, Building2, Mic, FileText, TrendingUp, BookOpen, ExternalLink } from "lucide-react";
import { joinCollege, myDrives, applyToDrive, myApplications, myAnnouncements, collegeByCode, myCourses, setMyCourseStatus, purchaseCourse, myStanding, myDriveAgent, myTasks, submitTask } from "@/lib/campusApi";
import { completePurchase } from "@/lib/razorpay";
import { driveCompanyBrief } from "@/lib/api";
import { X, ShieldCheck, Info } from "lucide-react";
import { Sparkles, Clock } from "lucide-react";
import { Bell } from "lucide-react";

const STAGES = ["applied", "shortlisted", "interview", "offer", "placed"];
const STAGE_STYLE = {
  applied: "bg-stone-100 text-stone-700",
  shortlisted: "bg-blue-100 text-blue-700",
  interview: "bg-amber-100 text-amber-700",
  offer: "bg-purple-100 text-purple-700",
  placed: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-600",
};

export default function CampusDrivesPage() {
  const [joined, setJoined] = useState(null); // null=unknown, false=not joined, true=joined
  const [drives, setDrives] = useState([]);
  const [apps, setApps] = useState([]);
  const [anns, setAnns] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await myDrives();
      setDrives(Array.isArray(data) ? data : []);
      setJoined(true);
      try { const { data: a } = await myApplications(); setApps(Array.isArray(a) ? a : []); } catch (_) {}
      try { const { data: an } = await myAnnouncements(); setAnns(Array.isArray(an) ? an : []); } catch (_) {}
    } catch (e) {
      setJoined(false);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="container-x py-16 text-stone-500 flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Loading campus…</div>;

  return (
    <div className="container-x py-10 md:py-14">
      <span className="overline">Campus</span>
      <h1 className="font-display text-4xl md:text-5xl mt-2">Your campus drives.</h1>
      <p className="text-stone-500 mt-2 max-w-lg leading-relaxed">Drives from your placement cell — apply to the ones you're eligible for, ranked by your readiness.</p>

      {joined === false ? (
        <JoinCard onJoined={load} />
      ) : (
        <>
          {anns.length > 0 && (
            <div className="mt-10" data-testid="cm-my-announcements">
              <h2 className="font-display text-2xl mb-4 flex items-center gap-2"><Bell size={20} className="text-[#C2410C]" /> From your placement cell</h2>
              <div className="space-y-3">
                {anns.slice(0, 5).map((a) => (
                  <div key={a.id} className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-medium text-stone-900">{a.title}</p>
                      <span className="text-xs text-stone-400 shrink-0">{a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}</span>
                    </div>
                    <p className="mt-1.5 text-sm text-stone-700 whitespace-pre-wrap">{a.body}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          <DriveAgentFeed onApplied={load} />
          <HiringTasks />
          <PlacementPrepCard />
          {apps.length > 0 && <ApplicationsTracker apps={apps} />}
          <CollegeCourses />
          <h2 className="font-display text-2xl mt-12 mb-4">Open drives</h2>
          {drives.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-12 text-center text-stone-500">
              No open drives right now. Check back soon — your placement cell will post drives here.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {drives.map((d) => <DriveCard key={d.drive_id} drive={d} onApplied={load} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ─────────── Application tracker (also reused by the Applications hub) ─────────── */
export function ApplicationsTracker({ apps }) {
  return (
    <div className="mt-10" data-testid="cm-applications">
      <h2 className="font-display text-2xl mb-4">Your applications</h2>
      <div className="space-y-3">
        {apps.map((a) => {
          const done = a.status !== "rejected";
          const activeIdx = STAGES.indexOf(a.status);
          return (
            <div key={a.app_id} className="rounded-2xl border border-stone-200 bg-white p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-stone-400">{a.company_name}</p>
                  <p className="font-display text-xl">{a.drive_title}</p>
                </div>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STAGE_STYLE[a.status] || STAGE_STYLE.applied}`}>{a.status}</span>
              </div>
              {a.status === "rejected" ? (
                <p className="mt-4 text-sm text-red-600">Not selected this time — keep raising your score for the next drive.</p>
              ) : (
                <div className="mt-4 flex items-center gap-1.5">
                  {STAGES.map((st, i) => (
                    <React.Fragment key={st}>
                      <div className="flex flex-col items-center gap-1">
                        <div className={`w-3 h-3 rounded-full ${i <= activeIdx ? "bg-emerald-500" : "bg-stone-200"}`} />
                        <span className={`text-[9px] font-mono uppercase ${i <= activeIdx ? "text-stone-700" : "text-stone-400"}`}>{st}</span>
                      </div>
                      {i < STAGES.length - 1 && <div className={`flex-1 h-0.5 ${i < activeIdx ? "bg-emerald-500" : "bg-stone-200"}`} />}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* The student's drive agent: for every open drive, exactly what to do —
   apply now (deadline urgency) or close a fixable score gap with a plan. */
function DriveAgentFeed({ onApplied }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [done, setDone] = useState({});
  const load = () => myDriveAgent().then(({ data }) => setData(data)).catch(() => setData({ items: [] }));
  useEffect(() => { load(); }, []);

  const apply = async (item) => {
    setBusy(item.drive_id);
    try {
      await applyToDrive(item.drive_id);
      setDone((x) => ({ ...x, [item.drive_id]: true }));
      onApplied?.(); load();
    } catch (_) {}
    finally { setBusy(null); }
  };

  const items = data?.items || [];
  if (!data || items.length === 0) return null;

  return (
    <div className="mt-10" data-testid="drive-agent">
      <h2 className="font-display text-2xl mb-1 flex items-center gap-2">
        <span className="inline-flex p-1.5 rounded-lg bg-stone-900 text-amber-300"><Sparkles size={15} /></span>
        Your drive agent
      </h2>
      <p className="text-sm text-stone-500 mb-4">Watching every open drive against your profile — here's what to do about each.</p>
      <div className="space-y-2.5">
        {items.map((it) => it.kind === "apply_now" ? (
          <div key={it.drive_id} className="flex flex-wrap items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3.5" data-testid={`agent-apply-${it.drive_id}`}>
            <CheckCircle2 size={17} className="text-emerald-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-stone-900">
                You're eligible for <span className="font-semibold">{it.title}</span> ({it.company_name}{it.ctc ? ` · ${it.ctc}` : ""})
              </p>
              {it.days_left != null && (
                <p className="text-xs text-[#C2410C] font-medium flex items-center gap-1 mt-0.5">
                  <Clock size={11} /> Closes {it.days_left === 0 ? "today" : `in ${it.days_left} day${it.days_left !== 1 ? "s" : ""}`}
                </p>
              )}
            </div>
            {done[it.drive_id] ? (
              <span className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1 shrink-0"><CheckCircle2 size={13} /> Applied</span>
            ) : (
              <button onClick={() => apply(it)} disabled={busy === it.drive_id}
                className="px-4 py-2 rounded-full bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-60 shrink-0">
                {busy === it.drive_id ? <Loader2 size={12} className="animate-spin inline" /> : "Apply now"}
              </button>
            )}
          </div>
        ) : (
          <div key={it.drive_id} className="flex flex-wrap items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3.5" data-testid={`agent-gap-${it.drive_id}`}>
            <TrendingUp size={17} className="text-amber-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-stone-900">
                You're <span className="font-semibold text-[#C2410C]">{it.gap} point{it.gap !== 1 ? "s" : ""} short</span> for {it.title} ({it.company_name})
              </p>
              <p className="text-xs text-stone-600 mt-0.5">{it.plan}{it.days_left != null ? ` Deadline in ${it.days_left} day${it.days_left !== 1 ? "s" : ""}.` : ""}</p>
            </div>
            <Link to="/app/interview" className="px-4 py-2 rounded-full bg-stone-900 text-amber-300 text-xs font-semibold hover:bg-stone-700 shrink-0">
              Boost my score
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Hiring-round tasks on drives the student applied to, with inline submit. */
function HiringTasks() {
  const [rows, setRows] = useState(null);
  const [urls, setUrls] = useState({});
  const [busy, setBusy] = useState(null);
  const load = () => myTasks().then(({ data }) => setRows(Array.isArray(data) ? data : [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const submit = async (t) => {
    const url = (urls[t.task_id] || "").trim();
    if (!url) return;
    setBusy(t.task_id);
    try { await submitTask(t.task_id, { url }); load(); } catch (_) {}
    finally { setBusy(null); }
  };

  if (!rows || rows.length === 0) return null;

  return (
    <div className="mt-10" data-testid="cm-my-tasks">
      <h2 className="font-display text-2xl mb-1 flex items-center gap-2"><FileText size={20} className="text-[#C2410C]" /> Hiring tasks</h2>
      <p className="text-sm text-stone-500 mb-4">Assignments from the drives you applied to — submit before the deadline.</p>
      <div className="space-y-3">
        {rows.map((t) => (
          <div key={t.task_id} className="rounded-2xl border border-stone-200 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs font-mono uppercase tracking-widest text-stone-400">{t.company_name} · {t.drive_title}</p>
                <p className="font-medium text-stone-900 mt-0.5">{t.title}{t.due && <span className="text-xs text-[#C2410C] ml-2">due {t.due}</span>}</p>
                <p className="text-sm text-stone-600 mt-1 whitespace-pre-wrap">{t.instructions}</p>
              </div>
              {t.my_submission && <span className="shrink-0 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">Submitted ✓</span>}
            </div>
            {t.my_submission ? (
              <p className="mt-3 pt-3 border-t border-stone-100 text-xs text-stone-500">
                Your submission: <a href={t.my_submission.url} target="_blank" rel="noreferrer" className="font-mono underline text-stone-700">{t.my_submission.url}</a> — resubmit below to replace it.
              </p>
            ) : null}
            <div className="mt-3 flex gap-2">
              <input value={urls[t.task_id] || ""} onChange={(e) => setUrls((x) => ({ ...x, [t.task_id]: e.target.value }))}
                data-testid={`task-url-${t.task_id}`} placeholder="Link to your work (GitHub, doc, drive…)"
                className="cm-inp flex-1" />
              <button onClick={() => submit(t)} disabled={busy === t.task_id || !(urls[t.task_id] || "").trim()}
                data-testid={`task-submit-${t.task_id}`}
                className="px-4 py-2 rounded-full bg-stone-900 text-white text-xs font-semibold hover:bg-stone-700 disabled:opacity-50 shrink-0">
                {busy === t.task_id ? <Loader2 size={12} className="animate-spin inline" /> : t.my_submission ? "Resubmit" : "Submit"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlacementPrepCard() {
  const [s, setS] = useState(null);
  useEffect(() => { myStanding().then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s?.joined) return null;
  const role = s.target_role;
  const score = s.wishire_score ?? 0;

  return (
    <div className="mt-10 rounded-3xl bg-stone-900 text-stone-50 p-6 md:p-7 relative overflow-hidden wsh-grain" data-testid="cm-prep-card">
      <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(400px 260px at 90% 10%, rgba(217,119,6,0.4), transparent 60%)" }} />
      <div className="relative flex flex-wrap items-center justify-between gap-5">
        <div>
          <span className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>Placement prep</span>
          <h2 className="font-display text-2xl md:text-3xl mt-1">
            {role ? <>Getting ready for <span className="italic text-amber-300">{role}</span>.</> : <>Get <span className="italic text-amber-300">placement-ready</span>.</>}
          </h2>
          <p className="text-sm text-stone-400 mt-1.5">
            {score === 0
              ? "Your Wishire Score is 0 — recruiters shortlist by it. A fresher mock interview is the fastest first step."
              : `Your Wishire Score is ${score}. Keep practicing — every mock and résumé revision moves it.`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/app/interview" data-testid="prep-mock-cta"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-amber-300 text-stone-900 hover:bg-amber-200 transition-colors text-sm font-medium">
            <Mic size={15} /> Fresher mock interview
          </Link>
          <Link to="/app/resume" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 border border-white/20 text-stone-100 hover:bg-white/20 transition-colors text-sm font-medium">
            <FileText size={15} /> Score my résumé
          </Link>
          <Link to="/app/interview?tab=practice" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 border border-white/20 text-stone-100 hover:bg-white/20 transition-colors text-sm font-medium">
            <TrendingUp size={15} /> Practice drills
          </Link>
        </div>
      </div>
    </div>
  );
}

function CollegeCourses() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => myCourses().then(({ data }) => setRows(Array.isArray(data) ? data : [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const mark = async (c, status) => {
    setBusy(c.course_id);
    try { await setMyCourseStatus(c.course_id, status); load(); } catch (_) {}
    finally { setBusy(null); }
  };
  const buy = async (c) => {
    setBusy(c.course_id);
    try {
      const { data } = await purchaseCourse(c.course_id);
      const done = await completePurchase(data); // Razorpay when live, instant in mock mode
      if (done.purchased) toast.success(`Enrolled in "${c.title}" — ₹${(done.price_inr||0).toLocaleString("en-IN")} paid.`);
      load(); if (c.url) window.open(c.url, "_blank");
    } catch (e) { toast.error(e?.message || e?.response?.data?.detail || "Could not complete purchase."); }
    finally { setBusy(null); }
  };
  const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

  if (!rows || rows.length === 0) return null;

  return (
    <div className="mt-12" data-testid="cm-my-courses">
      <h2 className="font-display text-2xl mb-4 flex items-center gap-2"><BookOpen size={20} className="text-[#C2410C]" /> Courses from your college</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.map((c) => (
          <div key={c.course_id} className="rounded-2xl border border-stone-200 bg-white p-5 flex flex-col">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-stone-900 flex items-center gap-1.5 flex-wrap">
                  {c.title}
                  {c.recommended && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Recommended for you</span>}
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${c.price_inr > 0 ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>{c.price_inr > 0 ? money(c.price_inr) : "Free"}</span>
                </p>
                {c.provider && <p className="text-xs text-stone-400 mt-0.5">{c.provider}</p>}
              </div>
              {c.my_status === "completed" ? (
                <span className="shrink-0 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">Completed</span>
              ) : c.my_status === "started" ? (
                <span className="shrink-0 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">In progress</span>
              ) : null}
            </div>
            {c.description && <p className="mt-2 text-sm text-stone-600 leading-relaxed">{c.description}</p>}
            <div className="mt-4 pt-3 border-t border-stone-100 flex items-center gap-2">
              {c.price_inr > 0 && !c.purchased ? (
                <button onClick={() => buy(c)} disabled={busy === c.course_id} data-testid={`crs-buy-${c.course_id}`}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-[#C2410C] text-white hover:bg-[#9a3412] disabled:opacity-60">
                  {busy === c.course_id ? <Loader2 size={14} className="animate-spin" /> : <>Buy · {money(c.price_inr)}</>}
                </button>
              ) : c.my_status !== "completed" && (
                c.my_status === "started" ? (
                  <button onClick={() => mark(c, "completed")} disabled={busy === c.course_id} data-testid={`crs-done-${c.course_id}`}
                    className="text-sm font-medium text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1.5 disabled:opacity-60">
                    {busy === c.course_id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Mark completed
                  </button>
                ) : (
                  <button onClick={() => { mark(c, "started"); if (c.url) window.open(c.url, "_blank"); }} disabled={busy === c.course_id} data-testid={`crs-start-${c.course_id}`}
                    className="text-sm font-medium text-[#C2410C] hover:text-[#9a3412] inline-flex items-center gap-1.5 disabled:opacity-60">
                    {busy === c.course_id ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />} Start course
                  </button>
                )
              )}
              {c.url && (
                <a href={c.url} target="_blank" rel="noreferrer" className="ml-auto text-xs text-stone-400 hover:text-stone-900 inline-flex items-center gap-1">
                  Open <ExternalLink size={11} />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Fresher roles commonly recruited per department — powers the target-role
// suggestions on the join form. Free text is always allowed.
const ROLE_SUGGESTIONS = [
  { match: /cse|computer|it|software|mca|bca/i, roles: ["Software Engineer", "Data Analyst", "QA Engineer", "Full-Stack Developer", "Data Scientist"] },
  { match: /ece|electronic|electrical|eee/i, roles: ["Embedded Engineer", "VLSI Engineer", "Software Engineer", "Hardware Design Engineer"] },
  { match: /mech|production|auto/i, roles: ["Design Engineer", "Production Engineer", "CAE Analyst", "Graduate Engineer Trainee"] },
  { match: /civil/i, roles: ["Site Engineer", "Structural Engineer", "Planning Engineer"] },
  { match: /mba|management|bba/i, roles: ["Business Analyst", "Marketing Associate", "HR Associate", "Operations Analyst", "Financial Analyst"] },
  { match: /com|finance|account|economics/i, roles: ["Financial Analyst", "Accountant", "Audit Associate", "Banking Associate"] },
];
const rolesForBranch = (branch) => {
  const hit = ROLE_SUGGESTIONS.find((r) => r.match.test(branch || ""));
  return hit ? hit.roles : ["Software Engineer", "Business Analyst", "Data Analyst", "Graduate Trainee"];
};

function JoinCard({ onJoined }) {
  const [f, setF] = useState({ invite_code: "", branch: "", section: "", cgpa: "", grad_year: "", target_role: "" });
  const [college, setCollege] = useState(null); // {name, departments} once code resolves
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  // Resolve the college (name + departments) from the code — fires as it's typed
  // (>= 6 chars) and on blur, so the department dropdown appears automatically.
  const lookup = async (raw) => {
    const code = String(raw ?? f.invite_code).trim().toUpperCase();
    if (code.length < 6) { setCollege(null); return; }
    try { const { data } = await collegeByCode(code); setCollege(data); }
    catch { setCollege(null); }
  };
  const onCode = (e) => {
    const v = e.target.value;
    setF((x) => ({ ...x, invite_code: v }));
    lookup(v);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.invite_code.trim()) { setErr("Enter your placement cell's invite code."); return; }
    if (college?.departments?.length && !f.branch) { setErr("Select your department."); return; }
    setBusy(true); setErr("");
    try {
      await joinCollege({
        invite_code: f.invite_code.trim().toUpperCase(),
        branch: f.branch.trim() || undefined,
        section: f.section.trim() || undefined,
        cgpa: f.cgpa !== "" ? Number(f.cgpa) : undefined,
        grad_year: f.grad_year !== "" ? Number(f.grad_year) : undefined,
        target_role: f.target_role.trim() || undefined,
      });
      onJoined();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not join. Check your invite code."); setBusy(false); }
  };

  const hasDepts = college?.departments?.length > 0;

  return (
    <div className="mt-10 max-w-lg rounded-3xl border border-stone-200 bg-white p-8">
      <span className="inline-flex p-3 rounded-2xl bg-stone-900 text-white"><GraduationCap size={20} /></span>
      <h2 className="font-display text-2xl mt-4">Join your placement cell</h2>
      <p className="text-sm text-stone-600 mt-1">Enter the invite code your college's placement cell shared, plus your academic details (used to check drive eligibility).</p>
      <form onSubmit={submit} className="mt-5 space-y-3">
        <input value={f.invite_code} onChange={onCode} onBlur={() => lookup()} data-testid="cm-join-code" placeholder="Invite code (e.g. C07BFEB4)" className="cm-inp font-mono tracking-widest uppercase" />
        {college && (
          <div className="flex items-center gap-2.5 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2" data-testid="cm-join-college">
            {college.logo && <img src={college.logo} alt={college.name} className="w-8 h-8 rounded-lg object-contain bg-white border border-emerald-100" />}
            <p className="text-sm text-emerald-800 font-medium">✓ {college.name}</p>
          </div>
        )}
        <div className="grid grid-cols-4 gap-3">
          {hasDepts ? (
            <select value={f.branch} onChange={set("branch")} data-testid="cm-join-branch" className="cm-inp col-span-2">
              <option value="">Department…</option>
              {college.departments.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          ) : (
            <input value={f.branch} onChange={set("branch")} data-testid="cm-join-branch" placeholder="Branch" className="cm-inp col-span-2" />
          )}
          <input value={f.section} onChange={set("section")} data-testid="cm-join-section" placeholder="Section (A)" maxLength={20} className="cm-inp" />
          <input type="number" step="0.1" min="0" max="10" value={f.cgpa} onChange={set("cgpa")} placeholder="CGPA" className="cm-inp font-mono" />
        </div>
        <input type="number" min="2000" max="2100" value={f.grad_year} onChange={set("grad_year")} placeholder="Graduation year" className="cm-inp font-mono w-full" />
        <div>
          <input list="cm-role-suggestions" value={f.target_role} onChange={set("target_role")} data-testid="cm-join-role"
            placeholder="Role you're preparing for (e.g. Software Engineer)" className="cm-inp w-full" />
          <datalist id="cm-role-suggestions">
            {rolesForBranch(f.branch).map((r) => <option key={r} value={r} />)}
          </datalist>
          <p className="mt-1 text-xs text-stone-400">Your mock interviews, résumé scoring and prep are tuned to this role — you can change it anytime.</p>
        </div>
        {err && <p className="text-sm text-red-700">{err}</p>}
        <button type="submit" disabled={busy} data-testid="cm-join-submit" className="btn-primary w-full justify-center disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <>Join <ArrowRight size={15} /></>}
        </button>
      </form>
    </div>
  );
}

function CompanyBriefModal({ drive, onClose }) {
  const [intel, setIntel] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    driveCompanyBrief(drive.drive_id)
      .then(({ data }) => setIntel(data.intel))
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load the company brief."));
  }, [drive.drive_id]);
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  const Section = ({ title, items }) => !items?.length ? null : (
    <div className="mt-5">
      <p className="text-xs font-mono uppercase tracking-widest text-stone-400">{title}</p>
      <ul className="mt-2 space-y-1.5">
        {items.map((x, i) => (
          <li key={i} className="text-sm text-stone-700 flex items-start gap-2"><span className="text-[#C2410C] mt-0.5">•</span> {x}</li>
        ))}
      </ul>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-xl bg-[#FDFBF7] rounded-3xl overflow-hidden max-h-[88vh] flex flex-col" data-testid="company-brief-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 shrink-0">
          <div>
            <h2 className="font-display text-2xl">About {drive.company_name}</h2>
            <p className="text-xs text-stone-500">Compiled from public sources — know the company before you sit for the drive.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 overflow-y-auto">
          {err ? (
            <p className="text-sm text-red-700">{err}</p>
          ) : !intel ? (
            <p className="text-sm text-stone-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Researching {drive.company_name} — first time takes ~30 seconds…</p>
          ) : (
            <>
              <p className="text-sm text-stone-700 leading-relaxed">{intel.overview}</p>
              <Section title="What they do" items={intel.what_they_do} />
              <Section title="Their fresher hiring process" items={intel.hiring_process} />
              <Section title="Interview tips from past candidates" items={intel.interview_tips} />
              <Section title="Good to know" items={intel.good_to_know} />
              {intel.sources?.length > 0 && (
                <p className="mt-5 text-[11px] text-stone-400">
                  Sources: {intel.sources.slice(0, 4).map((u, i) => (
                    <a key={u} href={u} target="_blank" rel="noreferrer" className="underline hover:text-stone-600">{`[${i + 1}]`}</a>
                  )).reduce((acc, el, i) => (i ? [...acc, " ", el] : [el]), [])}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function DriveCard({ drive, onApplied }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showBrief, setShowBrief] = useState(false);
  const e = drive.eligibility || {};

  const apply = async () => {
    setBusy(true); setErr("");
    try { await applyToDrive(drive.drive_id); onApplied(); }
    catch (ex) { setErr(ex?.response?.data?.detail || "Could not apply."); setBusy(false); }
  };

  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-6 flex flex-col">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-stone-400 flex items-center gap-1"><Building2 size={11} /> {drive.company_name}</p>
          <p className="font-display text-2xl mt-1">{drive.title}</p>
          <p className="text-sm text-stone-500">{drive.role}{drive.ctc ? ` · ${drive.ctc}` : ""}</p>
          {drive.company_about && <p className="text-sm text-stone-600 mt-2 leading-relaxed">{drive.company_about}</p>}
          {drive.deadline && <p className="text-xs text-[#C2410C] mt-1">Apply by {new Date(drive.deadline).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5 text-xs">
        {e.min_cgpa != null && <span className="px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">CGPA ≥ {e.min_cgpa}</span>}
        {e.min_wishire_score != null && <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Score ≥ {e.min_wishire_score}</span>}
        {(e.branches || []).map((b) => <span key={b} className="px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">{b}</span>)}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
        {drive.company_brief_approved && (
          <button onClick={() => setShowBrief(true)} data-testid={`about-link-${drive.drive_id}`}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-600 hover:text-stone-900 hover:underline">
            <Info size={12} /> About {drive.company_name}
          </button>
        )}
        {/* Mock test + drive prep are for ELIGIBLE students only — the mock is
            prep for a drive you can actually apply to. */}
        {drive.eligible && (
          <Link to={`/app/company-tests?drive=${drive.drive_id}&company=${encodeURIComponent(drive.company_name || "")}`}
            data-testid={`mock-link-${drive.drive_id}`}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#C2410C] hover:underline">
            <ShieldCheck size={12} /> Take a {drive.company_name} mock test
          </Link>
        )}
        {drive.eligible && (
          <Link to={`/app/campus/drives/${drive.drive_id}/prep`} data-testid={`prep-link-${drive.drive_id}`}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#C2410C] hover:underline">
            <Sparkles size={12} /> Past test patterns
          </Link>
        )}
        <Link to={`/app/companies?q=${encodeURIComponent(drive.company_name || "")}`} data-testid={`hub-link-${drive.drive_id}`}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-600 hover:text-[#C2410C] hover:underline">
          <Info size={12} /> {drive.company_name} prep hub
        </Link>
      </div>
      {showBrief && <CompanyBriefModal drive={drive} onClose={() => setShowBrief(false)} />}
      <div className="mt-4 pt-4 border-t border-stone-100">
        {drive.already_applied ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700"><CheckCircle2 size={16} /> Applied</span>
            {drive.has_assessment && (
              drive.assessment_attempted ? (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-stone-500" data-testid={`da-done-${drive.drive_id}`}><CheckCircle2 size={13} /> Assessment submitted</span>
              ) : (
                <Link to={`/app/campus/drives/${drive.drive_id}/assessment`} data-testid={`da-take-${drive.drive_id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-full bg-[#C2410C] text-white hover:bg-[#9a3412] transition-colors">
                  <Lock size={12} /> Take proctored assessment
                </Link>
              )
            )}
          </div>
        ) : drive.eligible ? (
          <button onClick={apply} disabled={busy} data-testid={`cm-apply-${drive.drive_id}`} className="btn-primary w-full justify-center disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <>Apply <Megaphone size={15} /></>}
          </button>
        ) : (
          <div className="text-sm">
            <span className="inline-flex items-center gap-1.5 text-stone-400"><Lock size={14} /> Not eligible yet</span>
            <ul className="mt-2 space-y-1.5">
              {(drive.ineligible_reasons || []).map((r, i) => {
                const reason = typeof r === "string" ? { message: r, fixable: false } : r;
                return (
                  <li key={i} className="text-xs text-stone-600 flex items-start gap-1.5">
                    <span className="text-red-500 mt-0.5">•</span> {reason.message}
                  </li>
                );
              })}
            </ul>
            {/* Coaching: only the Wishire Score is improvable — nudge the highest-impact actions */}
            {(drive.ineligible_reasons || []).some((r) => typeof r === "object" && r.type === "score") && (
              <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3">
                <p className="text-xs font-medium text-amber-800 flex items-center gap-1.5"><TrendingUp size={13} /> Close the gap</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Link to="/app/interview" className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-[#C2410C] transition-colors"><Mic size={12} /> Mock interview</Link>
                  <Link to="/app/resume" className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full border border-stone-300 text-stone-700 hover:border-stone-900 transition-colors"><FileText size={12} /> Improve résumé</Link>
                </div>
              </div>
            )}
          </div>
        )}
        {err && <p className="mt-2 text-sm text-red-700">{err}</p>}
      </div>
    </div>
  );
}
